import classNames from "classnames";
import { uniqueId } from "lodash";
import { Component } from "react";

import { EntityTypeFilter } from "@app/exports/exportModels";
import { QESCheckbox } from "@app/shared/qes-standards";
import { FeatureFlagContextProps, Features, withFeatureFlagContext } from "@app/feature-flags";
import { MultiselectDropdown } from "@app/shared/checkbox-dropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/SingleSelectDropdown";

type AccuracySelectEntityTypeFilterProps = FeatureFlagContextProps & {
  disabled: boolean;
  onEntityTypeIncludeChange: (selected: boolean, isPractitioner: boolean) => void;
  onEntityTypeIncludeChangeDropdown: (selectedItems: DropdownItem<string>[]) => void;
  entityTypeFilter: EntityTypeFilter;
}

class WrappedAccuracySelectEntityTypeFilter extends Component<AccuracySelectEntityTypeFilterProps> {
  private readonly practitionerDropdownItem: DropdownItem<string> = { key: "practitioner", name: "1 - Practitioner", };
  private readonly serviceLocationDropdownItem: DropdownItem<string> = { key: "servicelocation", name: "2 - Service Location", }

  public render() {
    const hasIncludes = this.props.entityTypeFilter.includePractitionerEntityType || this.props.entityTypeFilter.includeServiceLocationEntityType;
    const useInspectionUpdates = this.props.enabledFeatureFlags.has(Features.ENABLE_VIEW_BY_INSPECTION_UPDATES);

    const cbPractitionerId = uniqueId("include-practitioner");
    const cbServiceLocationId = uniqueId("include-servicelocation");

    return (
      <>
        <p className="lead mb0 font-weight-bold">Entity Type Filter{this.props.disabled && <span>: Selected</span>}</p>
        <p className="mb-1">Please select the entity types to include:</p>

        <div className={classNames("form-group row", { "text-danger": !hasIncludes })}>
          <label className="col-12 col-form-label ptb0 text-left">
            {!useInspectionUpdates && "Include"}{!hasIncludes && <i className="fa fa-exclamation-triangle mt5" style={{ position: "absolute" }} />}
          </label>

          <div className="col-12 mt0">
            {useInspectionUpdates ? (
              <MultiselectDropdown
                containerClassName="w-100"
                label="Entity Type Filter"
                customSelectInputText={this.getDropdownText()}
                hideLabel={true}
                items={[this.practitionerDropdownItem, this.serviceLocationDropdownItem]}
                testId="accuracy-select-entity-type-filter__dropdown"
                onSelectionChanged={(selectedItems) => { this.props.onEntityTypeIncludeChangeDropdown(selectedItems) }}
                disabled={this.props.disabled}
                selectedItems={this.getSelectedItems()}
              />
            ) : (
              <>
                <QESCheckbox
                  containerClassName="form-check"
                  className="form-check-input mt-0"
                  label="1 - Practitioner"
                  id={cbPractitionerId}
                  disabled={this.props.disabled}
                  value="practitioner"
                  checked={this.props.entityTypeFilter.includePractitionerEntityType}
                  onChange={(e) => this.props.onEntityTypeIncludeChange(e.target.checked, true)}
                  testId="accuracy-select-entitytype__practitioner"
                />
                <QESCheckbox
                  containerClassName="form-check"
                  className="form-check-input mt-0"
                  id={cbServiceLocationId}
                  label="2 - Service Location"
                  disabled={this.props.disabled}
                  value="servicelocation"
                  checked={this.props.entityTypeFilter.includeServiceLocationEntityType}
                  onChange={(e) => this.props.onEntityTypeIncludeChange(e.target.checked, false)}
                  testId="accuracy-select-entitytype__servicelocation"
                />
              </>
            )}
          </div>
        </div>
      </>
    );
  }

  private getDropdownText = () => {
    if (this.props.entityTypeFilter.includePractitionerEntityType
      && this.props.entityTypeFilter.includeServiceLocationEntityType) {
      return "Practitioner and Service Location";
    } else if (this.props.entityTypeFilter.includePractitionerEntityType) {
      return "Practitioner";
    } else if (this.props.entityTypeFilter.includeServiceLocationEntityType) {
      return "Service Location";
    }
    return "";
  }

  private getSelectedItems = () => {
    const items = [];
    if (this.props.entityTypeFilter.includePractitionerEntityType) {
      items.push(this.practitionerDropdownItem);
    }
    if (this.props.entityTypeFilter.includeServiceLocationEntityType) {
      items.push(this.serviceLocationDropdownItem);
    }
    return items;
  }
}

const AccuracySelectEntityTypeFilter = withFeatureFlagContext(WrappedAccuracySelectEntityTypeFilter);
export default AccuracySelectEntityTypeFilter;
